import { css } from '@emotion/react';
import { Button, Icon, Text } from '@inflearn/ds-react-next';
import { VerifyPhoneModal } from '@inflearn/modals';
import { faArrowRight } from '@inflearn/pro-regular-svg-icons';
import { useDisclosure } from '@mantine/hooks';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { AI_INTERVIEWS_URL } from '../../../constants/urls';
import useCheckLoginSession from '../../../hooks/useCheckLoginSession';
import { useMockInterviewVoucherFreeCreateMutation } from '../../../mutations/useMockInterviewVoucherFreeCreateMutation';
import { useLoginModalContext } from '../../../providers/ControlLoginModalContextProvider';
import useClientAccountCredentialsQuery from '../../../queries/useClientAccountCredentialsQuery';
import useMockInterviewVoucherQuery, {
  getMockInterviewVoucherQueryKey,
} from '../../../queries/useMockInterviewVoucherQuery';
import { eventNames } from '../../../utils/GTM/eventNames';
import { useTrackMixpanelEvent } from '../../../utils/GTM/useTrackMixpanelEvent';
import { MockInterviewConsentModal } from './MockInterviewConsentModal';
import { MockInterviewEnvironmentGuideModal } from './MockInterviewEnvironmentGuideModal';
import { LANDING_EASE, LANDING_FONT, LANDING_MOBILE_MQ, LANDING_SPACE } from './landing/constants';
import {
  type EnvironmentGuideVariant,
  getEnvironmentGuideVariant,
} from './utils/getEnvironmentGuideVariant';

/**
 * 모의면접 시작 버튼.
 * 로딩/로그인/전화인증/이용권 상태를 분기하고,
 * 각 분기의 비즈니스 로직은 하위 분기 컴포넌트에 위임한다.
 */
export function MockInterviewStartButton() {
  const { isLoggedIn, isLoading: isCheckLoginSessionLoading } = useCheckLoginSession();
  const { data, isLoading: isGetMockInterviewVoucherLoading } = useMockInterviewVoucherQuery({});
  const credentialsQuery = useClientAccountCredentialsQuery({ enabled: isLoggedIn });
  const isPhoneVerified = Boolean(credentialsQuery.data?.phoneNumber);
  const isGetClientAccountCredentialsLoading = isLoggedIn && credentialsQuery.isInitialLoading;
  // MockInterviewButton은 분기에 따라 잦은 마운트/언마운트를 겪으므로, 그 안에서 훅을 호출하면
  // useClientAccountProfileQuery가 매번 새로 구독되어 인증 실패 시 요청이 끊임없이 재시도된다.
  // 분기 전환에 영향받지 않는 이 최상위에서 한 번만 구독해 하위로 내려준다.
  const { trackMixpanelEvent } = useTrackMixpanelEvent();

  // 이용권 여러 개를 각자 조건으로 훑는다: 진행 중 > 리포트 생성중 > 미사용 > 리포트 완료 순으로 우선한다.
  const vouchers = data?.items ?? [];
  const onAirVoucher = vouchers.find((voucher) => voucher.mockInterview?.status === 'ON_AIR');
  const generatingVoucher = vouchers.find(
    (voucher) => voucher.mockInterview?.status === 'REPORT_GENERATING',
  );
  const unusedVoucher = vouchers.find((voucher) => !voucher.isUsed);
  const reportVoucher = vouchers.find(
    (voucher) => voucher.mockInterview?.status === 'REPORT_GENERATED',
  );

  if (
    isCheckLoginSessionLoading ||
    isGetMockInterviewVoucherLoading ||
    isGetClientAccountCredentialsLoading
  ) {
    return (
      <MockInterviewButton
        label="모의면접 시작하기"
        trackMixpanelEvent={trackMixpanelEvent}
        loading
        disabled
      />
    );
  }

  if (!isLoggedIn) {
    return <AuthButton trackMixpanelEvent={trackMixpanelEvent} />;
  }

  if (!isPhoneVerified) {
    return <PhoneButton trackMixpanelEvent={trackMixpanelEvent} />;
  }

  if (onAirVoucher?.mockInterview?.mockInterviewId != null) {
    return (
      <ContinueMockInterviewButton
        mockInterviewId={onAirVoucher.mockInterview.mockInterviewId}
        trackMixpanelEvent={trackMixpanelEvent}
      />
    );
  }

  if (generatingVoucher != null) {
    return <GeneratingReportButton />;
  }

  if (unusedVoucher != null) {
    return <StartMockInterviewButton needsIssue={false} trackMixpanelEvent={trackMixpanelEvent} />;
  }

  if (reportVoucher?.mockInterview?.mockInterviewId != null) {
    return (
      <ReportMockInterviewButton
        mockInterviewId={reportVoucher.mockInterview.mockInterviewId}
        trackMixpanelEvent={trackMixpanelEvent}
      />
    );
  }

  return <StartMockInterviewButton needsIssue={true} trackMixpanelEvent={trackMixpanelEvent} />;
}

type MockInterviewButtonLabel = '모의면접 시작하기' | '모의면접 이어하기' | '보고서 보러가기';

type TrackMixpanelEvent = ReturnType<typeof useTrackMixpanelEvent>['trackMixpanelEvent'];

type MockInterviewButtonProps = {
  label: MockInterviewButtonLabel;
  trackMixpanelEvent: TrackMixpanelEvent;
  loading?: boolean;
  disabled?: boolean;
  skipEnvironmentGuide?: boolean;
  onClick?: () => void;
};

/**
 * 모든 분기 버튼의 시각 표현을 담당하는 프레젠테이셔널 컴포넌트(다크 글래스 CTA).
 * 클릭 시 모바일이거나 PC라도 화면이 좁으면 분기별 onClick 대신 환경 안내 모달을 띄운다.
 */
function MockInterviewButton({
  label,
  trackMixpanelEvent,
  skipEnvironmentGuide = false,
  onClick,
  ...props
}: MockInterviewButtonProps) {
  const [guideVariant, setGuideVariant] = useState<EnvironmentGuideVariant | null>(null);

  const handleClick = () => {
    trackMixpanelEvent(eventNames.CLICK_BUTTON_AT_MOCK_INTERVIEW, { label });

    const variant = skipEnvironmentGuide ? null : getEnvironmentGuideVariant();

    if (variant) {
      setGuideVariant(variant);
      return;
    }

    onClick?.();
  };

  return (
    <>
      <Button
        type="button"
        css={styles.cta}
        rightSection={<Icon icon={faArrowRight} size={18} color="#fff" />}
        onClick={handleClick}
        {...props}
      >
        <Text component="span" css={styles.ctaLabel}>
          {label}
        </Text>
      </Button>
      {guideVariant && (
        <MockInterviewEnvironmentGuideModal
          opened
          variant={guideVariant}
          onClose={() => setGuideVariant(null)}
        />
      )}
    </>
  );
}

type WithTrackMixpanelEvent = {
  trackMixpanelEvent: TrackMixpanelEvent;
};

/** 로그인 분기: 미로그인 상태에서 클릭 시 로그인 모달을 연다. */
function AuthButton({ trackMixpanelEvent }: WithTrackMixpanelEvent) {
  const { open } = useLoginModalContext();

  return (
    <MockInterviewButton
      label="모의면접 시작하기"
      trackMixpanelEvent={trackMixpanelEvent}
      onClick={open}
    />
  );
}

/**
 * 전화인증 분기: 미인증 상태에서 클릭 시 인증 모달을 연다.
 * 인증 성공 시 크레덴셜 쿼리를 무효화하면 리렌더되어 상위 컨테이너가 다음 분기로 넘긴다.
 */
function PhoneButton({ trackMixpanelEvent }: WithTrackMixpanelEvent) {
  const queryClient = useQueryClient();
  const [isPhoneModalOpen, phoneModalHandlers] = useDisclosure(false);

  const handleVerifySuccess = () => {
    queryClient.invalidateQueries(useClientAccountCredentialsQuery.queryKey);
    phoneModalHandlers.close();
  };

  return (
    <>
      <MockInterviewButton
        label="모의면접 시작하기"
        trackMixpanelEvent={trackMixpanelEvent}
        onClick={phoneModalHandlers.open}
      />
      <VerifyPhoneModal
        isOpen={isPhoneModalOpen}
        onClose={phoneModalHandlers.close}
        spot="mock-interview"
        onVerifySuccess={handleVerifySuccess}
      />
    </>
  );
}

type StartMockInterviewButtonProps = WithTrackMixpanelEvent & {
  /** 이용권이 없어 무료 발급이 선행되어야 하는지 여부 */
  needsIssue: boolean;
};

/**
 * 새 면접 시작 분기: 이용권 없음/미사용 이용권/재응시 등 새 면접을 시작하는 경우 렌더된다.
 * 클릭 시 항상 동의 모달을 열고, 동의 후 (필요 시 발급 →) 시작 페이지로 이동한다.
 */
function StartMockInterviewButton({
  needsIssue,
  trackMixpanelEvent,
}: StartMockInterviewButtonProps) {
  const queryClient = useQueryClient();
  const { mutate, isLoading: isIssuing } = useMockInterviewVoucherFreeCreateMutation({
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getMockInterviewVoucherQueryKey() });
      },
    },
  });
  const [isConsentModalOpen, consentModalHandlers] = useDisclosure(false);

  const handleConsentAgree = () => {
    consentModalHandlers.close();

    // 이용권이 없으면 발급 후 이동, 있으면 바로 이동
    if (needsIssue) {
      mutate(undefined, {
        onSuccess: () => {
          window.location.href = AI_INTERVIEWS_URL.NEW();
        },
      });
      return;
    }

    window.location.href = AI_INTERVIEWS_URL.NEW();
  };

  return (
    <>
      <MockInterviewButton
        label="모의면접 시작하기"
        trackMixpanelEvent={trackMixpanelEvent}
        loading={isIssuing}
        onClick={consentModalHandlers.open}
      />
      <MockInterviewConsentModal
        opened={isConsentModalOpen}
        onClose={consentModalHandlers.close}
        onAgree={handleConsentAgree}
      />
    </>
  );
}

type MockInterviewSessionButtonProps = WithTrackMixpanelEvent & {
  mockInterviewId: number;
};

/** 이어하기 분기: 진행 중(ON_AIR) 면접이 있으면 렌더되며, 클릭 시 세션 페이지로 이동한다. */
function ContinueMockInterviewButton({
  mockInterviewId,
  trackMixpanelEvent,
}: MockInterviewSessionButtonProps) {
  const handleClick = () => {
    window.location.href = AI_INTERVIEWS_URL.SESSION(mockInterviewId);
  };

  return (
    <MockInterviewButton
      label="모의면접 이어하기"
      trackMixpanelEvent={trackMixpanelEvent}
      onClick={handleClick}
    />
  );
}

/** 리포트 생성중 분기: 방금 끝난 면접의 리포트를 생성하는 동안 렌더되며, 클릭할 수 없다. */
function GeneratingReportButton() {
  return (
    <Button type="button" css={styles.cta} disabled>
      <Text component="span" css={styles.ctaLabel}>
        보고서 생성중
      </Text>
    </Button>
  );
}

/** 보고서 분기: 보고서 생성 완료(REPORT_GENERATED) 시 렌더되며, 클릭 시 보고서 페이지로 이동한다. */
function ReportMockInterviewButton({
  mockInterviewId,
  trackMixpanelEvent,
}: MockInterviewSessionButtonProps) {
  const handleClick = () => {
    window.location.href = AI_INTERVIEWS_URL.REPORT(mockInterviewId, { from: 'landing' });
  };

  return (
    <MockInterviewButton
      label="보고서 보러가기"
      trackMixpanelEvent={trackMixpanelEvent}
      skipEnvironmentGuide
      onClick={handleClick}
    />
  );
}

const styles = {
  cta: css({
    flex: 'none',
    height: 48,
    minWidth: 200,
    padding: `0 ${LANDING_SPACE.ctaPadX}`,
    borderRadius: 10,
    border: 'none',
    background: '#171719',
    color: '#fff',
    fontSize: LANDING_FONT.cta,
    fontWeight: 600,
    transition: `background ${LANDING_EASE}, transform ${LANDING_EASE}`,
    '&:hover': {
      background: '#2e2f33',
    },
    '&:active': {
      transform: 'scale(.98)',
    },
    '&:disabled, &[data-disabled]': {
      opacity: 0.4,
      cursor: 'not-allowed',
    },
    [LANDING_MOBILE_MQ]: {
      flex: '1 0 0',
      width: '100%',
      minWidth: 0,
    },
  }),
  ctaLabel: css({
    color: '#fff',
    fontSize: LANDING_FONT.cta,
    fontWeight: 600,
    letterSpacing: '-0.01em',
  }),
};
